import {groupBy, groupByUniqueKey, partition} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React, {useState, ComponentType, useLayoutEffect, useMemo} from 'react';
import ReactFlow, {
  Edge,
  Node,
  useNodesInitialized,
  NodeProps,
  ReactFlowProvider,
  Position,
  applyNodeChanges,
  useEdgesState,
  XYPosition,
  Panel,
  MarkerType,
} from 'reactflow';

import {assert, assertNotNullAndReturn} from 'Asserts';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {REACT_FLOW_PRO_OPTIONS} from 'processes/graph-builder/ReactFlowConstants';
import {GRAY_DARK, WHITE} from 'ui-library/Colors';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import HorizontalControls from 'ui-library/reactflow/controls/HorizontalControls';

import BulkSelectOptionsForMultipleItemsTree, {
  TreeNode,
  TreeNodeType,
} from '../BulkSelectOptionsForMultipleItemsTree';
import UnitOperationFlowchartNode, {NodeType, UnitOperationFields} from './UnitOperationFlowchartNode';

// Basis: reactflow internal coordinate space
export const FIRST_DEST_COLUMN_X = 0;
export const HIGHEST_DEST_NODE_Y = 0;
export const NODE_HORIZONTAL_GAP = 64;
export const NODE_VERTICAL_GAP = 50;

const STATIC_PROPS: Partial<React.ComponentProps<typeof ReactFlow>> = {
  nodesConnectable: false,
  nodesDraggable: false,
  selectNodesOnDrag: true,
  panOnScroll: true,
  panOnDrag: true,
  selectionOnDrag: true,
  selectionKeyCode: null,
  multiSelectionKeyCode: 'Shift',
  zoomOnScroll: false,
  zoomOnPinch: true,
  defaultViewport: {x: 600, y: 30, zoom: 1},
  proOptions: REACT_FLOW_PRO_OPTIONS,
};

const NODE_TYPES: Record<NodeType, ComponentType<NodeProps>> = {
  [NodeType.SOURCE_PMEI]: UnitOperationFlowchartNode,
  [NodeType.DESTINATION_PMEI]: UnitOperationFlowchartNode,
};

export interface UnitOpLinkEdge {
  sourcePMEIId: string;
  destinationPMEIId: string;
}

export type SourcePMEINode = Node<UnitOperationFields, NodeType.SOURCE_PMEI>;
export type DestinationPMEINode = Node<UnitOperationFields, NodeType.DESTINATION_PMEI>;

interface UnitOpLinkingFlowchartEditorProps {
  initialLinks: Array<UnitOpLinkEdge>;
  onChangeLinks: (links: Array<UnitOpLinkEdge>) => void;
  sourcePMEIs: Array<UnitOperationFields>;
  destinationPMEIs: Array<UnitOperationFields>;
}

export default function UnitOpLinkingFlowchartEditor(props: UnitOpLinkingFlowchartEditorProps): JSX.Element {
  return (
    <ReactFlowProvider>
      <ReactFlowWrapper fullWidth={true}>
        <PresentationalUnitOpLinkingFlowchartEditor {...props} />
      </ReactFlowWrapper>
    </ReactFlowProvider>
  );
}

function PresentationalUnitOpLinkingFlowchartEditor(props: UnitOpLinkingFlowchartEditorProps): JSX.Element {
  const orderedDestPMEIsInColumns = useMemo(
    () => getOrderedDestPMEIsInColumns(props.destinationPMEIs),
    [props.destinationPMEIs]
  );
  const intraRunMethodEdges = useMemo(
    () => makeIntraRunMethodEdges(orderedDestPMEIsInColumns),
    [orderedDestPMEIsInColumns]
  );
  const [unitOpLinkEdges, setUnitOpLinkEdges, onUnitOpLinkEdgesChange] = useEdgesState<UnitOpLinkEdge>(
    props.initialLinks.map((data) => makeUnitOpLinkEdge(data))
  );
  const [srcNodes, setSrcNodes] = useState<Array<SourcePMEINode>>(
    makeInitialSourcePMEINodes(props.sourcePMEIs, props.initialLinks)
  );
  const [destNodes, setDestNodes] = useState<Array<DestinationPMEINode>>(
    makeInitialDestinationPMEINodes(orderedDestPMEIsInColumns)
  );
  const areNodesInitialized = useNodesInitialized();

  const selectedDestNodes: Array<DestinationPMEINode> = destNodes.filter((node) => node.selected);

  // Detect if node membership (add/remove) or dimensions (populated by reactflow) have changed
  const srcNodeLayoutDepsHash = JSON.stringify([
    ...srcNodes.map(({data: {pmeiId}, height, width}) => ({pmeiId, height, width})),
    ...destNodes.map(({data: {pmeiId}, height, width}) => ({pmeiId, height, width})),
  ]);
  // Re-layout nodes when nodes are added or removed. The flow is:
  // 1) (render 1) Nodes are updated in state (added/removed/contents changed)
  // 2) (render 1) Reactflow queues node dimensions state updates via onNodesChange
  // 3) (render 2) This useLayoutEffect computes node positioning based on latest dimensions
  useLayoutEffect(
    () => {
      if (!areNodesInitialized) {
        return;
      }
      const positionsByPMEIId = getNodePositionsByPMEIId(srcNodes, destNodes, orderedDestPMEIsInColumns);
      function getNodesWithUpdatedPositions<N extends Node>(nodes: Array<N>): Array<N> {
        return produce(nodes, (nodes) => {
          for (const node of nodes) {
            if (positionsByPMEIId[node.data.pmeiId] != null) {
              node.position = positionsByPMEIId[node.data.pmeiId];
            }
          }
        });
      }
      setSrcNodes(getNodesWithUpdatedPositions);
      setDestNodes(getNodesWithUpdatedPositions);
    },
    // Can't use srcNodes or destNodes as dependencies because the reference changes every time state is set,
    // resulting in an infinite loop. Instead use srcNodeLayoutDepsHash to only detect changes we care about.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [areNodesInitialized, props.destinationPMEIs, props.sourcePMEIs, srcNodeLayoutDepsHash]
  );

  function onChangeLinkedSourcePMEIIds(linkedSourcePMEIIds: Array<string>): void {
    // Replace all edges for the selected destination nodes
    const uniqueSourcePMEIIds = Array.from(new Set(linkedSourcePMEIIds));
    const selectedDestPMEIIds = new Set(selectedDestNodes.map((node) => node.data.pmeiId));
    const withoutOldEdges = unitOpLinkEdges.filter(
      ({data}) => !selectedDestPMEIIds.has(assertNotNullAndReturn(data).destinationPMEIId)
    );
    const newEdges = Array.from(selectedDestPMEIIds).flatMap((destPMEIId) =>
      uniqueSourcePMEIIds.map((sourcePMEIId) =>
        makeUnitOpLinkEdge({
          sourcePMEIId,
          destinationPMEIId: destPMEIId,
        })
      )
    );
    const finalEdges = [...withoutOldEdges, ...newEdges];

    // Add any new source nodes for these linked PMEIs and clean up any orphaned source nodes without edges
    // We can't just compute all source nodes from finalEdges because we need to preserve other state (like
    // selection) in the source node objects.
    const withoutOrphanedNodes = srcNodes.filter((node) =>
      finalEdges.some((edge) => assertNotNullAndReturn(edge.data).sourcePMEIId === node.data.pmeiId)
    );
    const newNodes = uniqueSourcePMEIIds
      .filter((pmeiId) => !srcNodes.some((node) => node.data.pmeiId === pmeiId))
      .map((sourcePMEIId) =>
        makeSourcePMEINode(props.sourcePMEIs.find(({pmeiId}) => pmeiId === sourcePMEIId)!)
      );
    const finalSrcNodes = [...withoutOrphanedNodes, ...newNodes];

    // Update state and notify parent
    setSrcNodes(finalSrcNodes);
    setUnitOpLinkEdges(finalEdges);
    props.onChangeLinks(finalEdges.map(({data}) => assertNotNullAndReturn(data)));
  }

  return (
    <div className={css(styles.fullScreenContainer)}>
      <ReactFlow
        nodes={[...srcNodes, ...destNodes]}
        edges={unitOpLinkEdges.concat(intraRunMethodEdges)}
        nodeTypes={NODE_TYPES}
        // Handle non-sidebar interactions (eg. selection) and reactflow populating node dimensions
        onNodesChange={(nodeChanges) => {
          const [srcNodeChanges, destNodeChanges] = partition(
            // eslint-disable-next-line benchlint/no-in-operator
            nodeChanges.filter((nc) => 'id' in nc),
            (nc) => getNodeType(nc.id) === NodeType.SOURCE_PMEI
          );
          setSrcNodes((ns) => applyNodeChanges(srcNodeChanges, ns) as Array<SourcePMEINode>);
          setDestNodes((ns) => applyNodeChanges(destNodeChanges, ns) as Array<DestinationPMEINode>);
        }}
        onEdgesChange={onUnitOpLinkEdgesChange}
        {...STATIC_PROPS}
      >
        {selectedDestNodes.length > 0 && (
          <Panel position="top-right" style={{margin: '0px', height: '100%'}}>
            <SourcePMEIPickerSidebar
              allPossibleSourcePMEIs={props.sourcePMEIs}
              selectedDestPMEIIds={selectedDestNodes.map(({data}) => data.pmeiId)}
              edges={unitOpLinkEdges.map(({data}) => data!)}
              onChangeLinkedSourcePMEIIds={onChangeLinkedSourcePMEIIds}
            />
          </Panel>
        )}
        <HorizontalControls />
        <DefaultBackground />
      </ReactFlow>
    </div>
  );
}

function mapSourcesToSelectedDestinations(
  edges: Array<UnitOpLinkEdge>,
  selectedDestPMEIIds: Array<string>
): {[destPMEIId: string]: Set<string>} {
  const destinationsToSources: {[destPMEIId: string]: Set<string>} = {};
  for (const destPMEIId of selectedDestPMEIIds) {
    destinationsToSources[destPMEIId] = new Set<string>();
  }
  const selectedDestinationsSet = new Set(selectedDestPMEIIds);

  for (const edge of edges) {
    const {sourcePMEIId, destinationPMEIId} = edge;
    if (selectedDestinationsSet.has(destinationPMEIId)) {
      destinationsToSources[destinationPMEIId].add(sourcePMEIId);
    }
  }
  return destinationsToSources;
}

function SourcePMEIPickerSidebar(props: {
  selectedDestPMEIIds: Array<string>;
  allPossibleSourcePMEIs: Array<UnitOperationFields>;
  onChangeLinkedSourcePMEIIds: (linkedSourcePMEIIds: Array<string>) => void;
  edges: Array<UnitOpLinkEdge>;
}): JSX.Element {
  // PMEIs are grouped by procedureRunName and then by methodName
  const pmeisGroupedByProcedureAndMethod = new Map<string, Map<string, Array<UnitOperationFields>>>();

  props.allPossibleSourcePMEIs.forEach((pmei) => {
    assert(pmei.procedureRunName != null, 'unit operation instance expected to have a procedure run name');
    if (!pmeisGroupedByProcedureAndMethod.has(pmei.procedureRunName)) {
      pmeisGroupedByProcedureAndMethod.set(
        pmei.procedureRunName,
        new Map<string, Array<UnitOperationFields>>()
      );
    }
    const methodsMap = pmeisGroupedByProcedureAndMethod.get(pmei.procedureRunName)!;
    if (!methodsMap.has(pmei.methodName)) {
      methodsMap.set(pmei.methodName, []);
    }
    methodsMap.get(pmei.methodName)!.push(pmei);
  });

  const uniqueOrderedProcedureRunNames: Array<string> = [
    ...new Set( // Sets retain insertion order
      props.allPossibleSourcePMEIs
        .map(({procedureRunName}) => procedureRunName)
        .filter((name): name is string => name != null)
    ),
  ];

  const allSourceProcedureGroupItems: Array<TreeNode> = uniqueOrderedProcedureRunNames.map(
    (procedureRunName) => {
      const methodsForThisProcedure =
        pmeisGroupedByProcedureAndMethod.get(procedureRunName) ||
        new Map<string, Array<UnitOperationFields>>();
      const uniqueOrderedMethodNamesForProcedure = [
        ...new Set(
          props.allPossibleSourcePMEIs
            .filter((pmei) => pmei.procedureRunName === procedureRunName)
            .map(({methodName}) => methodName)
        ),
      ];

      const childrenMethodGroupItems: Array<TreeNode> = uniqueOrderedMethodNamesForProcedure.map(
        (methodName) => {
          const pmeisForThisMethod = methodsForThisProcedure.get(methodName) || [];

          const childrenPMEINodes: Array<TreeNode> = pmeisForThisMethod.map((pmeiDetails) => ({
            id: pmeiDetails.pmeiId,
            type: TreeNodeType.SELECTABLE_OPTION,
            label: `${pmeiDetails.conditionName} | Replicate ${pmeiDetails.replicateNumber}`,
          }));

          return {
            groupId: `methodgroup-${procedureRunName}-${methodName}`,
            label: methodName,
            type: TreeNodeType.GROUP,
            children: childrenPMEINodes,
          };
        }
      );

      return {
        groupId: `procedurerungroup-${procedureRunName}`,
        label: procedureRunName,
        type: TreeNodeType.GROUP, // This is now a top-level group
        children: childrenMethodGroupItems,
      };
    }
  );

  const selectedOptionIdsByItemId = mapSourcesToSelectedDestinations(props.edges, props.selectedDestPMEIIds);

  return (
    <div className={css(styles.sidebar)}>
      <BulkSelectOptionsForMultipleItemsTree
        selectedOptionIdsByItemId={selectedOptionIdsByItemId}
        onSelectOptions={(newSelectedOptionIdsByItemId) => {
          const pmeiIds = Object.values(newSelectedOptionIdsByItemId).flatMap((selectedOptionIds) =>
            Array.from(selectedOptionIds)
          );
          props.onChangeLinkedSourcePMEIIds(pmeiIds);
        }}
        optionsTree={allSourceProcedureGroupItems}
      />
    </div>
  );
}

/** Compute positions for source and destination nodes. This supports nodes of varying dimensions.
 *
 * - Source nodes are laid out on the left, destination nodes on the right.
 * - Source nodes are laid out in a single vertical column: ordered by run, then method, then condition, then
 *   replicate.
 * - Destination nodes are laid out such that each method gets its own vertical column. In each method column,
 *   nodes are ordered by condition, then replicate.
 * - All source and destination node columns are vertically centered against each other.
 */
function getNodePositionsByPMEIId(
  srcNodes: Array<SourcePMEINode>,
  destNodes: Array<DestinationPMEINode>,
  orderedDestPMEIsInColumns: Array<Array<UnitOperationFields>>
): Record<string, XYPosition> {
  assert(
    new Set(destNodes.map(({data}) => data.procedureRunName)).size === 1,
    'Only destination PMEIs from one run is supported because we rely on method name uniqueness for column layout'
  );

  // Source nodes are in a single column.
  const orderedSrcColumnNodes = srcNodes.toSorted((a, b) => sortComparePMEIs(a.data, b.data));

  // Figure out which dest nodes go in which dest node column.
  const destNodeByPMEIId = groupByUniqueKey(destNodes, ({data: {pmeiId}}) => pmeiId);
  const orderedDestNodesInColumns = orderedDestPMEIsInColumns.map((pmeis) =>
    pmeis.map(({pmeiId}) => destNodeByPMEIId[pmeiId])
  );

  // Figure out Y-offset of each column for vertical centering.
  const srcColumnHeight = getHeightOfNodeColumn(orderedSrcColumnNodes);
  const destColumnHeights = orderedDestNodesInColumns.map((nodes) => getHeightOfNodeColumn(nodes));
  const maxDestColumnHeight = Math.max(...destColumnHeights);
  const srcNodesYOffset = (maxDestColumnHeight - srcColumnHeight) / 2;
  const destColumnYOffsets = destColumnHeights.map((height) => (maxDestColumnHeight - height) / 2);

  // Node widths are not fixed, so we need to dynamically calculate the X-offset for each column.
  const srcColumnX =
    FIRST_DEST_COLUMN_X -
    NODE_HORIZONTAL_GAP -
    Math.max(...orderedSrcColumnNodes.map((node) => node.width ?? 0));
  const destColumnCumulativeXOffsets = orderedDestNodesInColumns.reduce((offsets, nodes, i) => {
    if (i === 0) {
      offsets.push(FIRST_DEST_COLUMN_X);
      return offsets;
    }
    const prevOffset = offsets[i - 1];
    const maxNodeWidth = Math.max(...orderedDestNodesInColumns[i - 1].map((node) => node.width ?? 0));
    const nextOffset = prevOffset + maxNodeWidth + NODE_HORIZONTAL_GAP;
    offsets.push(nextOffset);
    return offsets;
  }, [] as Array<number>);

  // Calculate positions for each node.
  const positionsByPMEIId: Record<string, XYPosition> = {};
  orderedSrcColumnNodes.forEach((node, i) => {
    if (i === 0) {
      positionsByPMEIId[node.data.pmeiId] = {x: srcColumnX, y: HIGHEST_DEST_NODE_Y + srcNodesYOffset};
    } else {
      const prevNode = orderedSrcColumnNodes[i - 1];
      positionsByPMEIId[node.data.pmeiId] = {
        x: FIRST_DEST_COLUMN_X - NODE_HORIZONTAL_GAP - (node.width ?? 0),
        // Node heights are not fixed, calc Y position based on previous node Y position and height.
        y: positionsByPMEIId[prevNode.data.pmeiId].y + (prevNode.height ?? 0) + NODE_VERTICAL_GAP,
      };
    }
  });
  orderedDestNodesInColumns.forEach((nodes, columnIndex) => {
    nodes.forEach((node, nodeIndex) => {
      if (nodeIndex === 0) {
        positionsByPMEIId[node.data.pmeiId] = {
          x: destColumnCumulativeXOffsets[columnIndex],
          y: HIGHEST_DEST_NODE_Y + destColumnYOffsets[columnIndex],
        };
      } else {
        const prevNode = nodes[nodeIndex - 1];
        positionsByPMEIId[node.data.pmeiId] = {
          x: destColumnCumulativeXOffsets[columnIndex],
          // Node heights are not fixed, calc Y position based on previous node Y position and height.
          y: positionsByPMEIId[prevNode.data.pmeiId].y + (prevNode.height ?? 0) + NODE_VERTICAL_GAP,
        };
      }
    });
  });
  return positionsByPMEIId;
}

function sortComparePMEIs(dataA: UnitOperationFields, dataB: UnitOperationFields): number {
  return (
    dataA.procedureRunOrderKey - dataB.procedureRunOrderKey ||
    dataA.methodOrderKey - dataB.methodOrderKey ||
    dataA.conditionOrderKey - dataB.conditionOrderKey ||
    dataA.replicateNumber - dataB.replicateNumber
  );
}

function getHeightOfNodeColumn(nodesInColumn: Array<Node>): number {
  const nodeHeights = nodesInColumn.reduce((nodeHeights, node) => nodeHeights + (node.height ?? 0), 0);
  return nodeHeights + Math.max(0, nodesInColumn.length - 1) * NODE_VERTICAL_GAP;
}

function getNodeType(nodeId: string): NodeType {
  return nodeId.startsWith('source') ? NodeType.SOURCE_PMEI : NodeType.DESTINATION_PMEI;
}

function getSourceNodeId(pmeiId: string): string {
  return `source-${pmeiId}`;
}
function getDestinationNodeId(pmeiId: string): string {
  return `dest-${pmeiId}`;
}

function makeSourcePMEINode(data: UnitOperationFields): SourcePMEINode {
  return {
    id: getSourceNodeId(data.pmeiId),
    position: {x: 0, y: 0}, // position will be calculated in useLayoutEffect after dimensions are known
    data,
    type: NodeType.SOURCE_PMEI,
    selectable: false,
    deletable: false,
    draggable: false,
    connectable: false,
    sourcePosition: Position.Right,
  };
}
function makeDestinationPMEINode(
  data: UnitOperationFields,
  nodeProps: {selectable: boolean}
): DestinationPMEINode {
  return {
    id: getDestinationNodeId(data.pmeiId),
    position: {x: 0, y: 0}, // position will be calculated in useLayoutEffect after dimensions are known
    data,
    type: NodeType.DESTINATION_PMEI,
    selectable: nodeProps.selectable,
    deletable: false,
    draggable: false,
    connectable: false,
    targetPosition: Position.Left,
  };
}
function makeUnitOpLinkEdge(data: UnitOpLinkEdge): Edge<UnitOpLinkEdge> {
  return {
    id: `unitop-link-edge-${data.sourcePMEIId}-${data.destinationPMEIId}`,
    source: getSourceNodeId(data.sourcePMEIId),
    target: getDestinationNodeId(data.destinationPMEIId),
    type: 'default',
    data,
    deletable: false,
    animated: true,
  };
}

function getOrderedDestPMEIsInColumns(
  destinationPMEIs: Array<UnitOperationFields>
): Array<Array<UnitOperationFields>> {
  const fullyOrderedDestPMEIs = destinationPMEIs.toSorted(sortComparePMEIs);
  const orderedDestMethodNames = [...new Set(fullyOrderedDestPMEIs.map(({methodName}) => methodName))];
  const orderedDestPMEIsByMethodName = groupBy(fullyOrderedDestPMEIs, ({methodName}) => methodName);
  return orderedDestMethodNames.map((methodName) =>
    assertNotNullAndReturn(orderedDestPMEIsByMethodName[methodName])
  );
}

function makeInitialSourcePMEINodes(
  sourcePMEIs: Array<UnitOperationFields>,
  links: Array<UnitOpLinkEdge>
): Array<SourcePMEINode> {
  const sourcesToRender = [...new Set(links.map(({sourcePMEIId}) => sourcePMEIId))];
  return sourcesToRender.map((sourcePMEIId) => {
    const pmeiData = sourcePMEIs.find(({pmeiId}) => pmeiId === sourcePMEIId);
    return makeSourcePMEINode(assertNotNullAndReturn(pmeiData));
  });
}
function makeInitialDestinationPMEINodes(
  orderedDestPMEIsInColumns: Array<Array<UnitOperationFields>>
): Array<DestinationPMEINode> {
  return orderedDestPMEIsInColumns.flatMap((pmeis, columnIndex) =>
    pmeis.map((pmei) => makeDestinationPMEINode(pmei, {selectable: columnIndex === 0}))
  );
}

/**
 * @returns reactflow edges representing intra-run method edges in the destination run.
 * We assume intra-run split pooling does not exist yet, so we can derive intra-run method edges by zipping
 * destination PMEIs in adjacent methods.
 */
function makeIntraRunMethodEdges(orderedDestPMEIsInColumns: Array<Array<UnitOperationFields>>): Array<Edge> {
  return orderedDestPMEIsInColumns.slice(1).flatMap((currentColumn, i) => {
    const prevColumn = orderedDestPMEIsInColumns[i];
    assert(
      prevColumn.length === currentColumn.length,
      'Varying number of PMEIs across methods are not supported.'
    );
    return currentColumn.map((currentPMEI, j) => {
      const prevPMEI = prevColumn[j];
      assert(
        prevPMEI.conditionName === currentPMEI.conditionName &&
          prevPMEI.replicateNumber === currentPMEI.replicateNumber,
        'Expected previous and current methods to have same condition/replicate setup'
      );
      return {
        id: `intra-run-method-edge-${prevPMEI.pmeiId}-${currentPMEI.pmeiId}`,
        source: getDestinationNodeId(prevPMEI.pmeiId),
        target: getDestinationNodeId(currentPMEI.pmeiId),
        type: 'default',
        data: {
          sourcePMEIId: prevPMEI.pmeiId,
          destinationPMEIId: currentPMEI.pmeiId,
        },
        deletable: false,
        animated: false,
        markerEnd: {type: MarkerType.ArrowClosed},
      };
    });
  });
}

export const TEST_EXPORTS = {
  SourcePMEIPickerSidebar,
  getNodePositionsByPMEIId,
  makeSourcePMEINode,
  makeDestinationPMEINode,
};

const styles = StyleSheet.create({
  sidebar: {
    width: '300px',
    height: '100%',
    border: `1px solid ${GRAY_DARK}`,
    background: WHITE,
  },
  fullScreenContainer: {
    margin: '0px',
    width: '100%',
    height: '100%',
  },
  maxHeight: {
    maxHeight: '100%',
    overflowY: 'auto',
  },
});
